import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const CWS_URL = "https://chromewebstore.google.com/detail/glosslink/pjfeijggadjjnciaoppapehckfjeogod";

const countTerms = (csv) => {
  const lines = csv.split(/\r?\n/).filter((l) => l.includes(",") && l.trim());
  if (!lines.length) return 0;
  const first = lines[0].toLowerCase();
  return first.includes("term") && first.includes("definition") ? lines.length - 1 : lines.length;
};

const parsePreview = (csv, max = 8) => {
  const lines = csv.split(/\r?\n/).filter((l) => l.includes(",") && l.trim());
  const first = (lines[0] || "").toLowerCase();
  const start = first.includes("term") && first.includes("definition") ? 1 : 0;
  return lines.slice(start, start + max).map((l) => {
    const cells = l.match(/("([^"]|"")*"|[^,]*)/g)?.filter((c) => c !== "") || l.split(",");
    const clean = (s) => (s || "").replace(/^"|"$/g, "").replace(/""/g, '"').trim();
    return { term: clean(cells[0]), def: clean(cells[1]) };
  }).filter((r) => r.term && r.def);
};

const ShareNav = () => (
  <nav className="bg-white border-b border-slate-200">
    <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2 font-extrabold text-2xl tracking-tight" data-testid="share-nav-logo"><span className="text-2xl">📒</span> GlossLink</Link>
      <a href={CWS_URL} target="_blank" rel="noopener noreferrer" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold text-sm" data-testid="share-nav-install">Add to Chrome</a>
    </div>
  </nav>
);

export function CreateSharePage() {
  const [name, setName] = useState("");
  const [csv, setCsv] = useState("");
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedInstr, setCopiedInstr] = useState(false);

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ""));
    reader.readAsText(file);
    if (!name) setName(file.name.replace(/\.csv$/i, ""));
  };

  const create = async () => {
    setError("");
    if (!name.trim()) { setError("Give your glossary a name (e.g., 'Acme Corp Glossary')."); return; }
    if (countTerms(csv) < 1) { setError("Paste or upload a CSV with at least one 'term,definition' line."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), csv }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong");
      setLink(`${window.location.origin}/g/${data.id}`);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const teamInstructions = link ? `Welcome! Set up ${name || "our team glossary"} (takes 2 minutes):

1. INSTALL — On your computer, open Chrome and install GlossLink:
   https://chromewebstore.google.com/detail/glosslink/pjfeijggadjjnciaoppapehckfjeogod

2. ACTIVATE YOUR SEAT (if our company bought seats) — Click the GlossLink icon in your toolbar → click "Upgrade" → on the page that opens, click "Log in" → enter your WORK email. It's pre-approved — no payment, no card.

3. LOAD THE GLOSSARY — Open this link and click "Add to my GlossLink":
   ${link}

Done! Every company term now highlights with its definition on everything you read — intranet, docs, tickets, PDFs. Hover any highlighted word to see the meaning.` : "";

  const copyInstructions = async () => {
    try { await navigator.clipboard.writeText(teamInstructions); setCopiedInstr(true); setTimeout(() => setCopiedInstr(false), 2500); } catch (e) {}
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) {}
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <ShareNav />
      <main className="max-w-2xl mx-auto px-6 py-14">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Share a glossary with your team</h1>
        <p className="mt-3 text-slate-600">Upload your master glossary once and get a link. Anyone who opens it clicks one button — and every term is highlighted with your definitions on every page they read.</p>

        {!link ? (
          <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
            <div>
              <label className="block text-sm font-bold mb-1">Glossary name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Acme Corp Glossary" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:outline-none focus:border-blue-600" data-testid="share-name-input" />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Your glossary CSV</label>
              <p className="text-xs text-slate-500 mb-2">Upload the CSV exported from GlossLink — or paste rows like <code className="bg-slate-100 px-1 rounded">NPO,Nothing by mouth</code> (one per line).</p>
              <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} className="block w-full text-sm mb-3" data-testid="share-file-input" />
              <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={8} placeholder={"term,definition\nNPO,Nothing by mouth\nPRN,As needed"} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-blue-600" data-testid="share-csv-input" />
              {countTerms(csv) > 0 && <div className="mt-1 text-xs text-green-700 font-semibold" data-testid="share-term-count">✓ {countTerms(csv)} terms detected</div>}
            </div>
            {error && <div className="text-sm text-red-600 font-semibold" data-testid="share-error">{error}</div>}
            <button onClick={create} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-3.5 rounded-xl text-lg" data-testid="share-create-btn">
              {busy ? "Creating..." : "Create share link →"}
            </button>
          </div>
        ) : (
          <div className="mt-8 bg-white border-2 border-green-500 rounded-2xl p-6 text-center" data-testid="share-success">
            <div className="text-3xl mb-2">🎉</div>
            <h2 className="text-xl font-extrabold">Your share link is ready</h2>
            <p className="text-sm text-slate-600 mt-1">Send it to your team — email, Slack, onboarding doc, anywhere.</p>
            <div className="mt-4 bg-slate-100 rounded-lg px-4 py-3 font-mono text-sm break-all" data-testid="share-link-text">{link}</div>
            <button onClick={copy} className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl" data-testid="share-copy-btn">
              {copied ? "✓ Copied!" : "Copy link"}
            </button>
            <div className="mt-6 text-left">
              <div className="font-bold text-sm mb-1">📋 Ready-made instructions for your team</div>
              <p className="text-xs text-slate-500 mb-2">Copy this and paste it into your welcome email, Slack, or onboarding doc — it walks every team member through setup.</p>
              <textarea readOnly value={teamInstructions} rows={10} className="w-full border border-slate-200 bg-slate-50 rounded-lg px-3 py-2 text-xs font-mono text-slate-700" data-testid="share-team-instructions" />
              <button onClick={copyInstructions} className="mt-2 w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-xl" data-testid="share-copy-instructions-btn">
                {copiedInstr ? "✓ Instructions copied!" : "Copy team instructions"}
              </button>
            </div>
            <button onClick={() => { setLink(""); setCsv(""); setName(""); }} className="mt-3 text-sm text-slate-500 underline" data-testid="share-new-btn">Create another</button>
          </div>
        )}

        <div className="mt-8 text-sm text-slate-500 bg-slate-100 rounded-xl p-4">
          <b>How your team uses it:</b> they install GlossLink (20-day free trial), open your link, and click "Add to my GlossLink" — done. Every term highlights instantly on every website and PDF they read.
        </div>
      </main>
    </div>
  );
}

export default function SharedGlossaryPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/share/${id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setNotFound(true));
  }, [id]);

  const addToGlossLink = () => {
    window.postMessage({ type: "glosslink-import", csv: data.csv, name: data.name }, "*");
    setSent(true);
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 font-sans">
        <ShareNav />
        <div className="max-w-xl mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-extrabold">Glossary not found</h1>
          <p className="mt-2 text-slate-600">This share link is invalid or was removed.</p>
          <Link to="/" className="mt-6 inline-block text-blue-600 underline font-semibold">Go to glosslink.app</Link>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-400 font-sans">Loading glossary…</div>;

  const preview = parsePreview(data.csv);
  const total = countTerms(data.csv);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <ShareNav />
      <main className="max-w-2xl mx-auto px-6 py-14">
        <div className="text-center">
          <div className="inline-block bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full mb-4">SHARED GLOSSARY</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="shared-glossary-name">{data.name}</h1>
          <p className="mt-2 text-slate-600" data-testid="shared-glossary-count">{total} terms — ready to light up on every page you read</p>
        </div>

        <div className="mt-8 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr><th className="px-4 py-2.5 font-bold">Term</th><th className="px-4 py-2.5 font-bold">Definition</th></tr>
            </thead>
            <tbody>
              {preview.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-2.5"><mark className="bg-yellow-200 border-b-2 border-yellow-500 rounded-sm px-1 font-semibold">{r.term}</mark></td>
                  <td className="px-4 py-2.5 text-slate-600">{r.def}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > preview.length && <div className="px-4 py-2.5 text-xs text-slate-400 border-t border-slate-100">+ {total - preview.length} more terms</div>}
        </div>

        <button onClick={addToGlossLink} className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl text-lg shadow-lg shadow-blue-600/20" data-testid="shared-add-btn">
          {sent ? "✓ Sent to GlossLink — check the confirmation popup" : "Add to my GlossLink →"}
        </button>

        {sent && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-slate-700" data-testid="shared-no-ext-help">
            <b>No confirmation popup appeared?</b> You need GlossLink installed first (free 20-day trial):
            <a href={CWS_URL} target="_blank" rel="noopener noreferrer" className="block mt-2 text-blue-600 underline font-semibold" data-testid="shared-install-link">Install GlossLink from the Chrome Web Store →</a>
            <span className="block mt-1 text-xs text-slate-500">Then come back to this page and click the button again.</span>
          </div>
        )}

        <p className="mt-8 text-center text-xs text-slate-400">Desktop Chrome only · GlossLink highlights these terms with their definitions on every website and PDF</p>
      </main>
    </div>
  );
}
